import {isFeatureEnabled} from '@github-ui/feature-flags'
import type {
  GraphQLType,
  IssueFieldSelection,
  IssueFieldSelectionValues,
  IssueFieldSingleSelectOption,
} from '@github-ui/issue-fields-shared/issue-field'
import {fromGraphQLType, getIssueFieldValue} from '@github-ui/issue-fields-shared/issue-field'
import type {IssueFieldsUsageEvent} from '@github-ui/issue-fields-shared/issue-fields-analytics'
import {reportIssueFieldsUsage} from '@github-ui/issue-fields-shared/issue-fields-analytics'
import {useInteractionTiming} from '@github-ui/issue-fields-shared/use-interaction-timing'
import {noop} from '@github-ui/noop'
import {ErrorBoundary} from '@github-ui/react-core/error-boundary'
import {Tooltip} from '@primer/react'
import {useCallback, useEffect, useState} from 'react'
import {useFragment, useRelayEnvironment} from 'react-relay'
import {graphql} from 'relay-runtime'
import type RelayModernEnvironment from 'relay-runtime/lib/store/RelayModernEnvironment'

import {
  getDeleteValue,
  getIssueFieldSelectionValue,
  isGraphQLType,
  type IssueFieldWithUnsavedValue,
  toIssueFieldWithUnsavedValue,
  toIssueFieldWithValue,
} from '../../utils/issue-field-helper'
import type {FieldsSectionFieldValues$key, IssueFieldDataType} from './__generated__/FieldsSectionFieldValues.graphql'
import type {FieldsSectionFragment$key} from './__generated__/FieldsSectionFragment.graphql'
import {AddIssueFieldButton} from './AddIssueFieldButton'
import {LABELS} from './constants/labels'
import {FieldsSelector} from './FieldSelector'
import {ErrorDialog, getEditor, getIssueFieldEditor, type SafeFieldValue} from './IssueFieldEditors'
import {commitSetIssueFieldValueMutation} from './mutations/set-issue-field-value-mutation'

export type CreateIssueFieldsSectionProps = {
  owner: string
  onSelectionChange: (fields: IssueFieldWithUnsavedValue[]) => void
  shortcutEnabled: boolean
}

// a shared helper for setting issue field value via mutation (new input shape)
function setIssueFieldValueHelper({
  environment,
  issueId,
  fieldId,
  fieldType,
  value,
  deleteValue,
  onError,
  action,
  interactionDuration,
  reportUsageEnabled,
  inProjectsPage,
}: {
  environment: RelayModernEnvironment
  issueId: string
  fieldId: string
  fieldType: GraphQLType
  value?: string
  deleteValue?: boolean
  onError?: () => void
  action: IssueFieldsUsageEvent['action']
  interactionDuration?: number
  reportUsageEnabled: boolean
  inProjectsPage?: boolean
}) {
  const startTime = Date.now()
  commitSetIssueFieldValueMutation({
    environment,
    input: {
      issueId,
      issueFields: [
        {
          fieldId,
          ...getIssueFieldValue(fieldType, value),
          ...(deleteValue && {delete: true}),
        },
      ],
    },
    onCompleted: () => {
      const duration = Date.now() - startTime
      reportIssueFieldsUsage(
        {
          page: inProjectsPage ? 'memex#show' : 'issue#show',
          action,
          fieldId,
          fieldType: fromGraphQLType(fieldType),
          status: 'success',
          duration,
          interactionDuration,
        },
        reportUsageEnabled,
      )
    },
    onError: error => {
      // eslint-disable-next-line no-console
      console.error('Error setting issue field value:', error)
      const duration = Date.now() - startTime
      reportIssueFieldsUsage(
        {
          page: inProjectsPage ? 'memex#show' : 'issue#show',
          action,
          fieldId,
          fieldType: fromGraphQLType(fieldType),
          status: 'error',
          errorDetails: error?.message,
          duration,
          interactionDuration,
        },
        reportUsageEnabled,
      )
      onError?.()
    },
  })
}

export function CreateIssueFieldsSection({owner, onSelectionChange, shortcutEnabled}: CreateIssueFieldsSectionProps) {
  const [fields, setFields] = useState<IssueFieldWithUnsavedValue[]>([])
  const [fieldsSet, setFieldsSet] = useState<string[]>([])
  const reportUsageEnabled = isFeatureEnabled('issue_fields_report_usage')
  const [interactionStart, markInteractionStart, resetInteractionStart] = useInteractionTiming()

  useEffect(() => {
    onSelectionChange(fields)
  }, [fields, onSelectionChange])

  const reportUsage = useCallback(
    (fieldId: string, fieldType: IssueFieldsUsageEvent['fieldType'], interactionDuration: number) => {
      resetInteractionStart()
      reportIssueFieldsUsage(
        {
          page: 'issue#new',
          action: 'create',
          fieldId,
          fieldType,
          status: 'success',
          interactionDuration,
        },
        reportUsageEnabled,
      )
    },
    [reportUsageEnabled, resetInteractionStart],
  )

  const onIssueFieldSelected = useCallback(
    (
      selectedField: IssueFieldSelection,
      selectedOption?: IssueFieldSingleSelectOption | null,
      dateValue?: Date | null,
    ) => {
      const now = Date.now()
      const duration = now - (interactionStart.current || now)

      setFields(prevFields => {
        const newFields = [...prevFields]
        if (selectedField.name && !newFields.some(f => f.field.name === selectedField.name)) {
          newFields.push(toIssueFieldWithUnsavedValue(selectedField, selectedOption || null, dateValue || null))
        }
        reportUsage(selectedField.id || '', fromGraphQLType(selectedField.dataType), duration)
        return newFields
      })
    },
    [interactionStart, reportUsage],
  )

  const onCommit = useCallback((fieldId: string, value: IssueFieldSelectionValues) => {
    setFields(prevFields => {
      return prevFields.map(field => {
        if (field.field.id === fieldId) {
          return toIssueFieldWithValue(field.field, value)
        }
        return field
      })
    })
  }, [])

  return (
    <>
      {fields.map((field, index) => {
        if (!field.field.id) return null
        const isLastItem = index === fields.length - 1

        const fieldValue = typeof field.value === 'object' && field.value !== null ? {...field, ...field.value} : field
        return getEditor({
          fieldValue: fieldValue as SafeFieldValue,
          readonly: false,
          hideDivider: isLastItem,
          onCommit,
        })
      })}
      <FieldsSelector
        owner={owner}
        fieldsSet={fieldsSet}
        setFieldsSet={setFieldsSet}
        onIssueFieldSelected={onIssueFieldSelected}
        shortcutEnabled={shortcutEnabled}
        markInteractionStart={markInteractionStart}
      />
    </>
  )
}

export type EditIssueFieldsSectionProps = {
  onIssueUpdate?: () => void
  singleKeyShortcutsEnabled: boolean
  issue: FieldsSectionFragment$key
  insideSidePanel?: boolean
  inProjectsPage?: boolean
}

export function EditIssueFieldsSection({
  issue,
  singleKeyShortcutsEnabled,
  inProjectsPage,
}: EditIssueFieldsSectionProps) {
  const [fieldsSet, setFieldsSet] = useState<string[]>([])
  const [mutationErrorPresent, setMutationErrorPresent] = useState<boolean>(false)
  const [errorDialogOpen, setErrorDialogOpen] = useState<boolean>(false)
  const environment = useRelayEnvironment()
  const reportUsageEnabled = isFeatureEnabled('issue_fields_report_usage')

  const [interactionStart, markInteractionStart, resetInteractionStart] = useInteractionTiming()

  const data = useFragment(
    graphql`
      fragment FieldsSectionFragment on Issue {
        id
        repository {
          owner {
            login
          }
        }
        viewerCanSetFields
        ...FieldsSectionFieldValues
      }
    `,
    issue,
  )
  const {
    repository: {
      owner: {login: owner},
    },
    viewerCanSetFields,
  } = data

  const handleErrors = useCallback(() => {
    setMutationErrorPresent(true)
  }, [])

  const onIssueFieldSelected = useCallback(
    (
      selectedField: IssueFieldSelection,
      selectedOption?: IssueFieldSingleSelectOption | null,
      dateValue?: Date | null,
    ) => {
      if (!selectedField.id) return
      let value: string = ''
      switch (selectedField.dataType) {
        case 'TEXT':
          value = ''
          break
        case 'SINGLE_SELECT':
          value = selectedOption ? selectedOption.id : ''
          break
        case 'DATE':
          value = formatDateForStorage(new Date(dateValue || '')) || ''
          break
      }
      setIssueFieldValueHelper({
        environment,
        issueId: data.id,
        fieldId: selectedField.id,
        fieldType: selectedField.dataType,
        value,
        onError: handleErrors,
        action: 'create',
        interactionDuration: interactionStart.current ? Date.now() - interactionStart.current : undefined,
        reportUsageEnabled,
        inProjectsPage,
      })
      resetInteractionStart()
    },
    [resetInteractionStart, environment, data.id, handleErrors, interactionStart, reportUsageEnabled, inProjectsPage],
  )

  return (
    <>
      <ErrorBoundary
        fallback={
          <div>
            <Tooltip text={LABELS.loadingFieldsTooltipMessage}>
              <span>
                <AddIssueFieldButton inactive anchorProps={{onClick: () => setErrorDialogOpen(true)}} onClick={noop} />
              </span>
            </Tooltip>
            {errorDialogOpen && (
              <ErrorDialog onClose={() => setErrorDialogOpen(false)} message={LABELS.loadingFieldsErrorMessage} />
            )}
          </div>
        }
      >
        <IssueFields issue={data} fieldsSet={fieldsSet} setFieldsSet={setFieldsSet} inProjectsPage={inProjectsPage} />
        {viewerCanSetFields && (
          <FieldsSelector
            owner={owner}
            fieldsSet={fieldsSet}
            setFieldsSet={setFieldsSet}
            onIssueFieldSelected={onIssueFieldSelected}
            shortcutEnabled={singleKeyShortcutsEnabled}
            markInteractionStart={markInteractionStart}
          />
        )}
      </ErrorBoundary>

      {mutationErrorPresent && (
        <ErrorDialog onClose={() => setMutationErrorPresent(false)} message={LABELS.mutationErrorMessage} />
      )}
    </>
  )
}

function IssueFields({
  issue,
  fieldsSet,
  setFieldsSet,
  inProjectsPage,
}: {
  issue: FieldsSectionFieldValues$key
  fieldsSet: string[]
  setFieldsSet: React.Dispatch<React.SetStateAction<string[]>>
  inProjectsPage?: boolean
}) {
  const environment = useRelayEnvironment()
  const [errorPresent, setErrorPresent] = useState<boolean>(false)
  const reportUsageEnabled = isFeatureEnabled('issue_fields_report_usage')
  const data = useFragment(
    graphql`
      fragment FieldsSectionFieldValues on Issue {
        id
        issueFieldValues(first: 25) {
          nodes {
            ... on IssueFieldTextValue {
              field {
                ... on IssueFieldText {
                  id
                  name
                  dataType
                }
              }
              value
            }
            ... on IssueFieldSingleSelectValue {
              field {
                ... on IssueFieldSingleSelect {
                  id
                  name
                  dataType
                }
              }
              name
              # eslint-disable-next-line relay/unused-fields // this is used in IssueFieldEditors.tsx
              color
              # eslint-disable-next-line relay/unused-fields // this is used in IssueFieldEditors.tsx
              description
            }
            ... on IssueFieldDateValue {
              field {
                ... on IssueFieldDate {
                  id
                  name
                  dataType
                }
              }
              value
            }
          }
        }
        viewerCanSetFields
      }
    `,
    issue,
  )

  const filteredFieldValues = (data.issueFieldValues?.nodes || []).filter(fv => !!fv && fv.field)

  const filteredFieldsNames = filteredFieldValues.map(fv => fv?.field?.name || '')

  const handleErrors = useCallback(() => {
    setErrorPresent(true)
  }, [])
  useEffect(() => {
    if (fieldsSet.join(',') !== filteredFieldsNames.join(',') && filteredFieldsNames.length > 0) {
      setFieldsSet(filteredFieldsNames)
    }
  }, [fieldsSet, filteredFieldsNames, setFieldsSet])

  const onCommitField = useCallback(
    (fieldId: string, fieldType: IssueFieldDataType, value: IssueFieldSelectionValues) => {
      if (!isGraphQLType(fieldType)) return
      const selectionValue = getIssueFieldSelectionValue(fieldType, value)
      setIssueFieldValueHelper({
        environment,
        issueId: data.id,
        fieldId,
        deleteValue: getDeleteValue(fieldType, selectionValue),
        value: selectionValue,
        fieldType,
        onError: handleErrors,
        action: value === '' || value === undefined ? 'delete' : 'update',
        reportUsageEnabled,
        inProjectsPage,
      })
    },
    [data.id, environment, handleErrors, inProjectsPage, reportUsageEnabled],
  )

  const fieldValues = filteredFieldValues.map((fieldValue, index) => {
    if (!fieldValue || !fieldValue.field || !fieldValue.field.id) return null
    const isLastItem = index === filteredFieldValues.length - 1
    if (fieldValue.field === undefined || fieldValue.field.dataType === undefined) return null

    return getIssueFieldEditor({
      errorPresent,
      setErrorPresent: () => setErrorPresent(false),
      fieldValue: fieldValue as SafeFieldValue,
      readonly: !data.viewerCanSetFields,
      hideDivider: isLastItem,
      onCommit: (fieldId: string, value: IssueFieldSelectionValues) =>
        onCommitField(fieldId, fieldValue.field!.dataType!, value),
    })
  })

  return <>{fieldValues}</>
}

// TODO: Do this in one shared place
// Helper function to format dates consistently and avoid timezone issues
// The function sets the time to noon UTC (12:00 PM) to normalize the date.
// Noon UTC is chosen because it minimizes the risk of date shifts caused by timezone differences.
// For example, if a date is stored without a specific time, converting it across timezones could
// result in the date being shifted forward or backward. By fixing the time at noon UTC, we ensure
// that the date remains consistent regardless of the user's timezone.
// This function is intended for formatting dates before storing them in a database or API.
function formatDateForStorage(date: Date | null): string | null {
  if (!date) return null
  const normalizedDate = new Date(Date.UTC(date.getFullYear(), date.getMonth(), date.getDate(), 12, 0, 0, 0))
  return normalizedDate.toISOString()
}

try{ CreateIssueFieldsSection.displayName ||= 'CreateIssueFieldsSection' } catch {}
try{ EditIssueFieldsSection.displayName ||= 'EditIssueFieldsSection' } catch {}
try{ IssueFields.displayName ||= 'IssueFields' } catch {}